<template>
  <div class="statistics-cards">
    <div class="stat-card">
      <div class="stat-card-top">
        <div class="stat-card-title-row">
          <div class="stat-card-icon">
            <i class="pi pi-wallet"></i>
          </div>
          <div class="stat-label">Итоговая сумма</div>
        </div>
        <i
          class="pi pi-info-circle stat-info-icon"
          v-tooltip.bottom="{
            value: 'Общая сумма расходов по всем кампаниям и сегментам',
            class: 'white-tooltip-with-arrow',
          }"
        ></i>
      </div>
      <div class="stat-value">
        <Skeleton
          v-if="isLoading"
          width="180px"
          height="2rem"
          class="stat-value-skeleton"
        />
        <template v-else>
          <span class="stat-value-rubles">{{ formatCostRubles(statistics.total_cost) }}</span>
          <span class="stat-value-separator">.</span>
          <span class="stat-value-kopeks">{{ formatCostKopeks(statistics.total_cost) }}</span>
          <span class="currency-symbol">₽</span>
        </template>
      </div>
    </div>

    <div class="stat-card">
      <div class="stat-card-top">
        <div class="stat-card-title-row">
          <div class="stat-card-icon">
            <i class="pi pi-users"></i>
          </div>
          <div class="stat-label">Количество пользователей</div>
        </div>
        <i
          class="pi pi-info-circle stat-info-icon"
          v-tooltip.bottom="{
            value:
              'Общее количество пользователей во всех сегментах (из счётчика сегментов)',
            class: 'white-tooltip-with-arrow',
          }"
        ></i>
      </div>
      <div class="stat-value">
        <Skeleton
          v-if="isLoading"
          width="150px"
          height="2rem"
          class="stat-value-skeleton"
        />
        <span v-else>{{ formatNumber(statistics.total_users) }}</span>
      </div>
    </div>

    <div class="stat-card">
      <div class="stat-card-top">
        <div class="stat-card-title-row">
          <div class="stat-card-icon">
            <i class="pi pi-database"></i>
          </div>
          <div class="stat-label">Количество сегментов</div>
        </div>
        <i
          class="pi pi-info-circle stat-info-icon"
          v-tooltip.bottom="{
            value: 'Общее количество сегментов в статистике',
            class: 'white-tooltip-with-arrow',
          }"
        ></i>
      </div>
      <div class="stat-value">
        <Skeleton
          v-if="isLoading"
          width="80px"
          height="2rem"
          class="stat-value-skeleton"
        />
        <span v-else>{{ formatNumber(statistics.segments_count) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumber } from "@/services/utils/Strings";
import Skeleton from "primevue/skeleton";

defineProps<{
  statistics: {
    total_cost: number;
    total_users: number;
    segments_count: number;
  };
  isLoading?: boolean;
}>();

const formatCostRubles = (costInKopeks: number): string => {
  const rubles = Math.floor(costInKopeks / 100);
  return formatNumber(rubles);
};

const formatCostKopeks = (costInKopeks: number): string => {
  const kopeks = costInKopeks % 100;
  return kopeks.toString().padStart(2, "0");
};
</script>

<style lang="scss" scoped>
.statistics-cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.stat-card {
  flex: 1;
  min-width: 280px;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);

  .stat-card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;

    .stat-card-title-row {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;

      .stat-card-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f5f5f7;
        border-radius: 8px;
        flex-shrink: 0;

        i {
          font-size: 20px;
          color: $main-link-color;
        }
      }

      .stat-label {
        font-size: 14px;
        color: #6b7280;
        font-weight: 400;
        line-height: 1.4;
      }
    }

    .stat-info-icon {
      font-size: 16px;
      color: #b0adb7;
      cursor: pointer;
      transition: color 0.2s ease;
      flex-shrink: 0;
      margin-left: 8px;

      &:hover {
        color: $main-link-color;
      }
    }
  }

  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: $main-font-color;
    line-height: 1.2;
    font-variant-numeric: lining-nums proportional-nums;
    letter-spacing: 0.28px;

    .stat-value-rubles {
      color: $main-font-color;
    }

    .stat-value-separator {
      font-size: 1.2rem;
      font-weight: 500;
      color: #9ca3af;
      margin: 0 1px;
    }

    .stat-value-kopeks {
      font-size: 1.2rem;
      font-weight: 500;
      color: #9ca3af;
    }

    .currency-symbol {
      font-size: 1.5rem;
      font-weight: 400;
      color: #9ca3af;
      margin-left: 4px;
    }

    .stat-value-skeleton {
      margin-top: 4px;
    }
  }
}

@media (max-width: 768px) {
  .statistics-cards {
    flex-direction: column;
  }

  .stat-card {
    min-width: 100%;
  }
}
</style>
